import { createClient } from "@supabase/supabase-js";
import { supabaseUrl, supabaseAnonKey } from "./config/supabaseConfig";
import { checkResponseForSessionDeath } from "./utils/auth/sessionLiveness";
import { checkResponseForMfaRequired } from "./utils/auth/mfaRequiredSignal";
import { observeCompanyLockResponse } from "./utils/company/detectCompanyLockResponse";
import { reportCompanyLockRefusal } from "./utils/company/officeLockDialogBus";
import { trackRequest } from "./utils/app/pendingWrites";

/**
 * Gate which URLs may hand an auth callback to supabase-js (KAN-481 / RD-SEC-018, 2026-10-21).
 * - NEVER an implicit-flow session (`#access_token=…&refresh_token=…`): anyone can mint one from
 *   their own account and aim a victim at it (login CSRF — the victim is silently signed into the
 *   attacker's FULL session). Every CrewVex email carries a `token_hash` link (recovery) or our own
 *   `?invite=` link (RD-SEC-045), which `/reset-password` and `/accept-invite` verify THEMSELVES
 *   with `verifyOtp`, so the SDK must not consume any session from the URL. No `code` either: the
 *   client is not `flowType: 'pkce'`. (A forwarded token_hash recovery link can still open a
 *   recovery-only session for someone else — bounded: no company access, the account email is shown
 *   on the form, and a global sign-out follows; that is Supabase's flow, not an app surface.)
 * - A stray token hash is also scrubbed from the address bar so a live token never sits in history.
 * - Only an `error` / `error_description` callback is still handed to the SDK, which then reports
 *   it and creates no session. On `/` that is for a recovery redirect only.
 * - Email magic-link tokens are not consumed here (no client-side magic-link sign-in).
 * - `params` is the SDK's merge of the hash AND the query string (query wins).
 */
export function detectSessionInUrl(url, params) {
	const path = url.pathname || "";
	const hasErrorPayload = Boolean(params.error || params.error_description);
	if ((params.access_token || params.refresh_token) && typeof window !== "undefined" && window.history?.replaceState) {
		try { window.history.replaceState(null, "", `${url.pathname}${url.search || ""}`); } catch { /* ignore */ }
	}
	// A token_hash recovery link is verified by ResetPassword itself on the Continue tap
	// (KAN-1033); it must NOT mark the recovery intent here — that flag outlives an
	// abandoned confirm card and would let a later ordinary session see the form.
	if (path === "/") return params.type === "recovery" && hasErrorPayload;
	if (path !== "/reset-password" && path !== "/accept-invite") return false;
	return hasErrorPayload;
}

const localStorageAdapter = {
	getItem: (key) => {
		try { return window.localStorage.getItem(key); } catch { return null; }
	},
	setItem: (key, value) => {
		try { window.localStorage.setItem(key, value); } catch { /* Safari private browsing; session won't persist but won't crash */ }
	},
	removeItem: (key) => {
		try { window.localStorage.removeItem(key); } catch { /* ignore */ }
	},
};

// Preconnect to the Supabase origin so DNS + TLS handshake completes before the
// first auth/REST request. Reads from the same supabaseUrl resolved by config,
// so dev and prod builds each preconnect to their own project automatically —
// no hardcoded URLs. crossorigin="anonymous" matches the supabase-js fetch mode
// (auth header, no cookies) so the warmed connection is reusable.
if (typeof document !== "undefined" && supabaseUrl) {
	try {
		const origin = new URL(supabaseUrl).origin;
		const link = document.createElement("link");
		link.rel = "preconnect";
		link.href = origin;
		link.crossOrigin = "anonymous";
		document.head.appendChild(link);
	} catch { /* malformed URL — skip warmup, browser handles conn setup the slow way */ }
}

// Defense in depth for the gotrue auth lock. supabase-js serializes EVERY auth
// call (getSession / getUser / mfa.listFactors / signOut / token refresh) behind
// one in-tab lock, and its fetch has no timeout. On iOS PWA resume a request
// that was in flight when the app was suspended can hang forever — the OS tears
// down the socket but the fetch promise never settles — so the lock is never
// released and every subsequent auth call deadlocks (stuck MFA status, frozen
// pull-to-refresh, impossible sign-out). A bounded timeout lets a dead auth
// request reject so the lock frees itself.
//
// SCOPED TO /auth/v1 ONLY: the same fetch is shared by PostgREST, Storage,
// Edge Functions and Realtime (SupabaseClient routes them all through
// global.fetch). Those carry large/slow-but-alive payloads (e.g. financial-PDF
// downloads), so they keep the default no-timeout behavior — we never cut them
// off. We also never override a caller-supplied AbortSignal, and fall back to a
// plain fetch where AbortSignal.timeout is unavailable.
const AUTH_FETCH_TIMEOUT_MS = 20000;

const nativeFetch =
	typeof window !== "undefined" && typeof window.fetch === "function"
		? window.fetch.bind(window)
		: undefined;

const requestUrl = (input) => {
	if (typeof input === "string") return input;
	if (typeof URL !== "undefined" && input instanceof URL) return input.href;
	return (input && input.url) || "";
};

// Session-death detection (2026-07-27 fix; corrected same day after a
// white-box review found the original wiring inert -- see
// src/utils/auth/sessionLiveness.js for the full story). `supabase.functions`
// is a GETTER on SupabaseClient that constructs a brand-new FunctionsClient
// on every property access, so patching `supabase.functions.invoke` once at
// module load (the original, broken approach) never touched what any of the
// 81 call sites actually invoked -- each one built its own fresh, unpatched
// client. The one interception point every Supabase sub-client shares and
// cannot route around is the `fetch` handed to `createClient({ global:
// { fetch } })` below (auth / PostgREST / Storage / Functions / Realtime all
// call through it). So: a non-OK response to a `/functions/v1/` OR `/rest/v1/`
// request fires a fire-and-forget liveness check against a CLONED Response --
// the real caller always receives the original, unread Response back.
// REST path: only probes when the body matches session_revoked / PT401
// (enforce_live_auth_session), not ordinary RLS 403s — see sessionLiveness.js.
const authTimeoutFetch = (input, init = {}) => {
	const base = nativeFetch || fetch;
	const url = requestUrl(input);
	const guardable =
		url.includes("/auth/v1/") &&
		!init.signal &&
		typeof AbortSignal !== "undefined" &&
		typeof AbortSignal.timeout === "function";
	const fetchPromise = guardable
		? base(input, { ...init, signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS) })
		: base(input, init);

	// KAN-886: let the silent app-update gate see in-flight writes (side branch only).
	trackRequest((init && init.method) || (input && input.method) || "GET", fetchPromise, url);

	const watchSessionDeath =
		url.includes("/functions/v1/") || url.includes("/rest/v1/");
	if (!watchSessionDeath) return fetchPromise;

	return fetchPromise.then((response) => {
		if (!response.ok) {
			try {
				void checkResponseForSessionDeath(response.clone());
			} catch {
				/* a liveness-check bug must never break the real fetch path */
			}
			// KAN-529: a "finish 2FA first" refusal routes to the enrollment
			// screen (ProtectedRoute listens) — its own clone, never the caller's.
			try {
				void checkResponseForMfaRequired(response.clone());
			} catch {
				/* same rule: never break the real fetch path */
			}
			// KAN-200 follow-up: a locked-company refusal opens the read-only dialog
			// (its own clone, fire-and-forget, auth excluded by the url gate above).
			observeCompanyLockResponse(url, response, reportCompanyLockRefusal);
		}
		return response;
	});
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
	auth: {
		persistSession: true,
		autoRefreshToken: true,
		detectSessionInUrl,
		storageKey: "crewvex-auth",
		storage: localStorageAdapter,
	},
	global: {
		fetch: authTimeoutFetch,
	},
});